import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Receipt } from '../../types/expense';
import { useApiService } from '../../hooks/use-api-service';
import { base64ToDataUrl } from '../../utils/image';
import { ArrowRight, TrendingUp, TrendingDown, Calendar, Receipt as ReceiptIcon, PoundSterling, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { UploadReceiptDialog } from './upload-receipt-dialog';
import { receiptTypeService } from '../../services/receiptTypeService';
import { getReceiptTypeLabel } from '../../config/receipt-types';
import { ReceiptImageViewer } from '../receipts/receipt-image-viewer';
import { toast } from 'sonner';
import { RecapIcon } from '../ui/recap-icon';

export function HomePage() {
  const { api, dataMode } = useApiService();
  const queryClient = useQueryClient();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const { data: processedReceipts = [] } = useQuery({
    queryKey: ['processedReceipts', dataMode],
    queryFn: () => api.getProcessedReceipts(),
  });

  const { data: unprocessedReceipts = [] } = useQuery({
    queryKey: ['unprocessedReceipts', dataMode],
    queryFn: () => api.getUnprocessedReceipts(),
  });

  const { data: receiptTypes = [] } = useQuery({
    queryKey: ['receiptTypes', dataMode],
    queryFn: () => receiptTypeService.getAll(dataMode === 'live'),
  });

  // Calculate smart summaries
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  const monthlyReceipts = processedReceipts.filter((receipt) => {
    const receiptDate = new Date(receipt.transactionDate || receipt.uploadedAt);
    return receiptDate.getMonth() === currentMonth && receiptDate.getFullYear() === currentYear;
  });

  const totalSpent = monthlyReceipts.reduce((sum, receipt) => sum + (receipt.amount || 0), 0);
  
  // Category breakdown (simplified - you can enhance this based on your data)
  const categoryBreakdown = monthlyReceipts.reduce((acc, receipt) => {
    const category = receipt.supplier || 'Other';
    acc[category] = (acc[category] || 0) + (receipt.amount || 0);
    return acc;
  }, {} as Record<string, number>);

  const topCategories = Object.entries(categoryBreakdown)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  // Get unprocessed receipts (sorted by upload date, most recent first)
  const displayedUnprocessedReceipts = [...unprocessedReceipts]
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

  const updateReceiptMutation = useMutation({
    mutationFn: ({ id, receipt }: { id: string; receipt: Partial<Receipt> }) => 
      api.updateReceipt(id, receipt),
    onSuccess: (data, { id, receipt }) => {
      queryClient.setQueryData(['unprocessedReceipts', dataMode], (prev: Receipt[] | undefined) => 
        prev?.map(r => r.id === id ? { ...r, ...receipt } : r)
      );
      queryClient.setQueryData(['processedReceipts', dataMode], (prev: Receipt[] | undefined) => 
        prev?.map(r => r.id === id ? { ...r, ...receipt } : r)
      );
      queryClient.invalidateQueries({ queryKey: ['unprocessedReceipts'] });
      queryClient.invalidateQueries({ queryKey: ['processedReceipts'] });
      toast.success('Receipt updated successfully!');
    },
    onError: () => {
      toast.error('Failed to update receipt');
    },
  });

  const handleCardClick = (receipt: Receipt) => {
    // Open the receipt viewer dialog
    setSelectedReceipt(receipt);
    setImageViewerOpen(true);
  };

  const handleGetStarted = () => {
    window.location.hash = 'receipts';
  };

  const handleUpdateReceipt = (receipt: Receipt) => {
    updateReceiptMutation.mutate({ id: receipt.id, receipt });
  };

  // Helper function to get receipt type for a receipt
  const getReceiptTypeForReceipt = (receipt: Receipt) => {
    if (!receipt.receiptType) return null;
    return receiptTypes.find((rt: { id: string }) => rt.id === receipt.receiptType);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Wood Desk Background */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 dark:from-amber-950 dark:via-orange-950 dark:to-amber-900"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 2px,
              rgba(139, 69, 19, 0.03) 2px,
              rgba(139, 69, 19, 0.03) 4px
            ),
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 2px,
              rgba(139, 69, 19, 0.02) 2px,
              rgba(139, 69, 19, 0.02) 4px
            )
          `,
        }}
      />
      
      {/* Subtle wood grain texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.3'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10">
        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center px-4 md:px-6 pt-12 md:pt-20 pb-8 md:pb-12 text-center">
          <div className="mb-4 md:mb-6 flex items-center justify-center">
            <RecapIcon className="h-16 w-16 md:h-20 md:w-20 drop-shadow-lg" />
          </div>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-3 md:mb-4">
            Recap
          </h1>
          <p className="text-base md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-2xl px-4">
            Snap it. Submit it. Sorted.
          </p>
          <Button 
            size="lg" 
            className="text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            onClick={handleGetStarted}
          >
            Get Started
            <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
          </Button>
        </div>

        {/* Smart Summary Section */}
        {processedReceipts.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8 md:pb-12">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-16">
              {/* Total Spent Card */}
              <Card className="p-6 bg-card">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm font-medium text-muted-foreground">This Month</div>
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="text-3xl font-bold mb-1">£{totalSpent.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground">{monthlyReceipts.length} receipts</div>
              </Card>

              {/* Average Spend Card */}
              <Card className="p-6 bg-card">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm font-medium text-muted-foreground">Average</div>
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="text-3xl font-bold mb-1">
                  £{monthlyReceipts.length > 0 ? (totalSpent / monthlyReceipts.length).toFixed(2) : '0.00'}
                </div>
                <div className="text-xs text-muted-foreground">per receipt</div>
              </Card>

              {/* Total Receipts Card */}
              <Card className="p-6 bg-card">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm font-medium text-muted-foreground">All Time</div>
                  <PoundSterling className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="text-3xl font-bold mb-1">{processedReceipts.length}</div>
                <div className="text-xs text-muted-foreground">total receipts</div>
              </Card>
            </div>

            {/* Desk View - Unprocessed Receipts */}
            {displayedUnprocessedReceipts.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold">Unprocessed Receipts</h2>
                  <Badge variant="secondary" className="text-sm">
                    {displayedUnprocessedReceipts.length} pending
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 auto-rows-max">
                  {displayedUnprocessedReceipts.map((receipt, index) => {
                    const rotations = [-2, 1.5, -1, 2, -1.5, 1, -2.5, 0.5];
                    const rotation = rotations[index % rotations.length];
                    const receiptType = getReceiptTypeForReceipt(receipt);
                    
                    return (
                      <div
                        key={receipt.id}
                        className="relative group cursor-pointer transition-all duration-500 ease-out"
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          zIndex: 10,
                        }}
                        onClick={() => handleCardClick(receipt)}
                      >
                        {/* Receipt Card */}
                        <div
                          className="
                            relative bg-white dark:bg-gray-900 border-2 border-border
                            shadow-[0_4px_12px_rgba(0,0,0,0.15),0_2px_4px_rgba(0,0,0,0.1)]
                            group-hover:shadow-[0_12px_32px_rgba(0,0,0,0.25),0_4px_8px_rgba(0,0,0,0.15)]
                            group-hover:-translate-y-3 group-hover:rotate-0 group-hover:border-primary
                            transition-all duration-300
                            p-4
                          "
                        >
                          {/* Receipt Thumbnail */}
                          <div className="mb-3 overflow-hidden rounded-sm bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-200 dark:to-orange-200">
                            {receipt.thumbnailData || receipt.imageData ? (
                              <img 
                                src={base64ToDataUrl(receipt.thumbnailData || receipt.imageData)} 
                                alt={receipt.supplier || 'Receipt'}
                                className="w-full object-cover h-32"
                                style={{
                                  filter: 'sepia(0.1) contrast(1.05)',
                                }}
                              />
                            ) : (
                              <div className="w-full h-32 flex items-center justify-center">
                                <ReceiptIcon className="h-12 w-12 text-amber-400" />
                              </div>
                            )}
                          </div>

                          {/* Receipt Info */}
                          <div className="space-y-2">
                            {/* Receipt Type Badge */}
                            <div className="mb-2">
                              {receiptType ? (
                                <Badge 
                                  className={`${receiptType.color} ${receiptType.textColor} text-xs px-2 py-0.5`}
                                >
                                  {getReceiptTypeLabel(receiptType.name)}
                                </Badge>
                              ) : (
                                <Badge 
                                  variant="secondary"
                                  className="text-xs px-2 py-0.5"
                                >
                                  Uncategorized
                                </Badge>
                              )}
                            </div>
                            
                            <h3 className="font-semibold text-sm truncate">
                              {receipt.supplier || 'Unknown Supplier'}
                            </h3>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">
                                {receipt.transactionDate 
                                  ? format(new Date(receipt.transactionDate), 'MMM dd')
                                  : format(new Date(receipt.uploadedAt), 'MMM dd')
                                }
                              </span>
                              <span className="font-bold">
                                £{(receipt.amount || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {processedReceipts.length === 0 && (
              <div className="relative group" style={{ transform: 'rotate(-1deg)' }}>
                <Card className="p-12 text-center bg-white dark:bg-gray-900 border-border hover:border-primary shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.15)] transition-all duration-300 hover:-translate-y-2 hover:rotate-0">
                  <ReceiptIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">No receipts yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Start by uploading your first receipt to track your expenses
                  </p>
                  <Button onClick={() => setUploadDialogOpen(true)}>
                    Upload Your First Receipt
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>

      <UploadReceiptDialog 
        open={uploadDialogOpen} 
        onOpenChange={setUploadDialogOpen} 
      />

      {selectedReceipt && (
        <ReceiptImageViewer
          open={imageViewerOpen}
          onOpenChange={setImageViewerOpen}
          imageData={selectedReceipt.imageData}
          receiptName={selectedReceipt.supplier || 'Receipt'}
          receipt={selectedReceipt}
          onUpdate={handleUpdateReceipt}
        />
      )}
    </div>
  );
}
